import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Stack,
    Button,
    Box,
    Alert,
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import FormInput from '../../components/inputs/FormInput';
import { useUpdatePassword } from '../../hooks/auth/useUpdatePassword';

const Security = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const updatePassword = useUpdatePassword();

    const schema = yup.object({
        current_password: yup
            .string()
            .required(t('profile.passwordCurrentRequired', 'Current password is required')),
        new_password: yup
            .string()
            .min(8, t('profile.passwordMin', 'Password must be at least 8 characters'))
            .required(t('profile.passwordNewRequired', 'New password is required')),
        new_password_confirmation: yup
            .string()
            .oneOf(
                [yup.ref('new_password')],
                t('profile.passwordsDoNotMatch', 'Passwords do not match')
            )
            .required(t('profile.passwordConfirmRequired', 'Please confirm the new password')),
    });

    const {
        register,
        handleSubmit,
        setError,
        reset,
        formState: { errors },
    } = useForm({ resolver: yupResolver(schema) });

    const onSubmit = (values) => {
        updatePassword.mutate(values, {
            onSuccess: () => {
                reset();
                navigate('/profile');
            },
            onError: (err) => {
                const apiErrors = err?.response?.data?.errors;
                if (apiErrors && typeof apiErrors === 'object') {
                    Object.keys(apiErrors).forEach((key) => {
                        const msg = Array.isArray(apiErrors[key]) ? apiErrors[key][0] : apiErrors[key];
                        if (key === 'current_password' || key === 'new_password' || key === 'new_password_confirmation') {
                            setError(key, { type: 'server', message: msg });
                        }
                    });
                }
            },
        });
    };

    return (
        <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 } }}>
            <Paper
                elevation={0}
                variant="outlined"
                sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}
                component="form"
                onSubmit={handleSubmit(onSubmit)}
            >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                    <Lock color="primary" />
                    <Box>
                        <Typography variant="h5" fontWeight={800}>
                            {t('profile.changePassword', 'Change password')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t(
                                'profile.changePasswordSubtitle',
                                'For security, the current password is required.'
                            )}
                        </Typography>
                    </Box>
                </Stack>

                <Stack spacing={2.5}>
                    <FormInput
                        type="password"
                        name="current_password"
                        label={t('profile.currentPassword', 'Current password')}
                        register={register('current_password')}
                        error={errors.current_password?.message}
                    />
                    <FormInput
                        type="password"
                        name="new_password"
                        label={t('profile.newPassword', 'New password')}
                        register={register('new_password')}
                        error={errors.new_password?.message}
                    />
                    <FormInput
                        type="password"
                        name="new_password_confirmation"
                        label={t('profile.confirmNewPassword', 'Confirm new password')}
                        register={register('new_password_confirmation')}
                        error={errors.new_password_confirmation?.message}
                    />
                </Stack>

                <Alert severity="info" sx={{ mt: 3 }}>
                    {t(
                        'profile.passwordTip',
                        'Use at least 8 characters mixing letters and numbers.'
                    )}
                </Alert>

                <Stack direction="row" spacing={1.5} sx={{ mt: 3 }} justifyContent="flex-end">
                    <Button
                        type="button"
                        variant="text"
                        onClick={() => navigate('/profile')}
                        disabled={updatePassword.isPending}
                    >
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={updatePassword.isPending}
                    >
                        {updatePassword.isPending
                            ? t('common.working', 'Working...')
                            : t('profile.savePassword', 'Save password')}
                    </Button>
                </Stack>
            </Paper>
        </Container>
    );
};

export default Security;
